import { z } from 'zod';

import type { BidStatusValue } from './messaging.ts';
import type { PlayerSummary } from './player.ts';

/**
 * Storico delle offerte di un calciatore.
 *
 * Il pannello realtime mostra le offerte della chiamata **in corso**, e le
 * dimentica appena si passa al calciatore successivo: è uno stato del browser,
 * non un archivio. Quando l'amministratore rilegge una rosa e vuole sapere
 * perché quel calciatore è costato 47, la fonte è il database.
 *
 * È materiale amministrativo come la sequenza: il fantallenatore vede l'esito,
 * non la ricostruzione di chi ha rilanciato e quando.
 */
export const BID_HISTORY_ROUTES = {
  /** Template Fastify; per un URL concreto usa `playerBidsPath()`. */
  byPlayer: '/auctions/:id/players/:playerId/bids',
} as const;

export function playerBidsPath(auctionId: string, playerId: string): string {
  return `/auctions/${encodeURIComponent(auctionId)}/players/${encodeURIComponent(playerId)}/bids`;
}

export const playerBidsParamsSchema = z.object({
  id: z.uuid('Identificativo asta non valido'),
  playerId: z.uuid('Identificativo calciatore non valido'),
});

export interface BidHistoryEntry {
  readonly id: string;
  readonly participantId: string;
  readonly participantName: string;
  readonly amount: number;
  readonly increment: number;
  /**
   * `VOIDED` per le offerte di un tentativo annullato (chiamata ricominciata,
   * rollback): restano a storico proprio perché spiegano un prezzo che
   * altrimenti sembrerebbe uscito dal nulla.
   */
  readonly status: BidStatusValue;
  /** Tentativo di chiamata a cui l'offerta appartiene. */
  readonly callId: string;
  /** ISO-8601. */
  readonly createdAt: string;
}

/**
 * Un'offerta che non è mai entrata: budget esaurito, countdown già scaduto,
 * prezzo mosso mentre si premeva.
 *
 * Vive nel registro degli eventi, non fra le offerte: non c'è nessuna riga
 * `Bid`, perché non è successo niente. Non porta un importo per la stessa
 * ragione — c'è il rialzo che qualcuno ha tentato, non un prezzo raggiunto.
 *
 * È materiale amministrativo come il resto di questo storico: dice chi stava
 * finendo il budget, e non è cosa che i rivali debbano leggere.
 */
export interface RejectedBidEntry {
  /** Id dell'evento di audit: qui non c'è una riga `Bid` a cui appoggiarsi. */
  readonly eventId: string;
  readonly participantId: string;
  readonly participantName: string;
  readonly increment: number;
  readonly code: string;
  readonly message: string;
  /** ISO-8601. */
  readonly createdAt: string;
}

export interface PlayerBidsResponse {
  readonly player: PlayerSummary;
  /** In ordine d'arrivo, dalla prima all'ultima. */
  readonly bids: readonly BidHistoryEntry[];
  /** Tentativi respinti, in ordine d'arrivo. Senza, il prezzo non si spiega. */
  readonly rejected: readonly RejectedBidEntry[];
  /**
   * Prezzo e acquirente dell'aggiudicazione attiva, `null` se il calciatore
   * non è (più) assegnato.
   */
  readonly purchase: {
    readonly amount: number;
    readonly participantId: string;
    readonly participantName: string;
    readonly createdAt: string;
  } | null;
}
